import { AxiosError } from "axios";

import { SubmitHandler, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button";
import Input from "@/components/inputs/Input";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { ROUTES } from "@/config/routes";
import { usePostLoginPassword, usePostSendCode } from "@/hooks/api/login.hooks";
import useModalRouter from "@/hooks/useModalRouter";

import AuthTitle from "../AuthTitle.";
import { AuthSignInForm } from "../types";

export interface AuthErrorResponse {
  message?: string;
}

const AuthPasswordModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useFormContext<AuthSignInForm>();

  const { mutate: passwordMutate, isPending: passwordPending, error } = usePostLoginPassword();
  const { mutate: otpMutate, error: otpError, isPending: otpPending } = usePostSendCode();

  const { closeModal, openModal } = useModalRouter();

  const onSubmit: SubmitHandler<AuthSignInForm> = (data: AuthSignInForm): void => {
    passwordMutate(data, {
      onSuccess: (res) => {
        const isActive = res.data.is_active;

        if (isActive) {
          closeModal();
          navigate(ROUTES.GATE.PROFILES);
          return;
        }

        openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_SESSIONS], { token: res.data.token });
      },
    });
  };

  const goToOtp = () => {
    const username = getValues("username");
    otpMutate(username, {
      onSuccess: () => {
        openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_OTP]);
      },
    });
  };

  const handleForgotPassword = () => {
    openModal([MODAL_ROUTES.FORGOT_PASSWORD, MODAL_ROUTES.FORGOT_PASSWORD_LOGIN]);
  };

  return (
    <div className="md:w-[25rem]">
      <AuthTitle goback title={t("Auth.enter-via-password")} subtitle={t("Auth.enter-password-message")} />
      <form onSubmit={handleSubmit(onSubmit)} className={"mt-4 w-full space-y-6 lg:mt-6"}>
        <div className={"space-y-3"}>
          <Input
            inputSize="medium"
            type={"password"}
            placeholder={t("Form.password")}
            register={register}
            name={"password"}
            validation={{
              required: { value: true, message: t("Form.required") },
              minLength: { value: 6, message: t("Auth.short-password") },
            }}
            errors={errors}
          />
          <span onClick={handleForgotPassword} className={"block cursor-pointer text-start text-lg/6"}>
            {t("General.forgot-password")}
          </span>
        </div>
        <Button size="medium" type={"submit"} disabled={passwordPending || otpPending}>
          {t("Actions.continue")}
        </Button>
      </form>
      {error && (
        <p className="mt-2 text-center text-sm text-error-1">
          {(error as AxiosError<AuthErrorResponse>)?.response?.data?.message}
        </p>
      )}
      <p
        className={
          "text-on-surface before:bg-surface-container after:bg-surface-container mt-6 flex items-center gap-[0.9375rem] text-sm/[1.25rem] uppercase before:block before:h-px before:w-full before:content-[''] after:block after:h-px after:w-full after:content-['']"
        }
      >
        {t("General.or")}
      </p>
      <div>
        <Button
          color={"white"}
          size="medium"
          onClick={goToOtp}
          disabled={passwordPending || otpPending}
          className={"mt-6"}
        >
          {t("Auth.enter-via-code")}
        </Button>
        {(otpError as AxiosError)?.response?.status ? (
          <p className={"mt-2 text-center text-sm text-error-1"}>{t("Auth.too-many-attempts")}</p>
        ) : null}
      </div>
    </div>
  );
};

export default AuthPasswordModal;
